import { MomentInput } from "moment/moment";
import buildUrl from "../utils/buildUrl";
import { baseUrl } from "./baseUrl";
import { IindividualSearchValues } from "../interfaces/IindividualSearchValues";

export const getAccountsRoute = ({
  page,
  size = 10,
  sort,
}: {
  page: number;
  size?: number;
  sort?: string;
}) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/accounts`,
    searchParams: {
      page,
      size,
      sort,
    },
  });

export const getAccountsOrganizationRoute = ({ page }: { page: number }) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/accounts/pageable-search`,
    searchParams: {
      page,
      size: 10,
      sort: "id",
      clientType: "ORGANIZATION",
    },
  });

export const getAccountsIndividualRoute = ({
  fullName,
  phone,
  email,
  id,
  agreementNumber,
  accountStatusId,
  agreementId,
  externalId,
  page,
  accTypeId,
}: IindividualSearchValues) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/accounts/pageable-search`,
    searchParams: {
      id,
      "individual.fullName": fullName,
      "individual.phone": phone,
      billDeliveryEmail: email,
      "agreement.id": agreementId,
      "agreement.agreementNumber": agreementNumber,
      "subscriber.externalId": externalId,
      accountStatusId,
      page,
      size: 10,
      sort: "id",
      clientType: "INDIVIDUAL",
      accTypeId,
    },
  });

export const fetchAccountsIndividualByNameRoute = (fullName: string) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/accounts/pageable-search`,
    searchParams: {
      "individual.fullName": fullName,
      page: 0,
      size: 20,
      clientType: "INDIVIDUAL",
    },
  });

export const getAccountVersionsRoute = ({ accountId }: { accountId: number }) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/accounts/${accountId}/versions`,
  });

export const getOrganizationVersionsRoute = ({ organizationId }: { organizationId: number }) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/organizations/${organizationId}/versions`,
  });

export const getIndividualVersionsRoute = ({ individualId }: { individualId: number }) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/individuals/${individualId}/versions`,
  });

export const getSubscriberVersionsRoute = ({ organizationId }: { organizationId: number }) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/subscribers/${organizationId}/versions`,
  });

export const getAccountsPageableSearchRoute = ({
  fullName,
  shortName,
  propertyTypeId,
  propertyStringValue,
  externalId,
  agreementId,
  accountStatusId,
  agreementNumber,
  billDeliveryEmail,
  id,
  page,
  size = 10,
  sort = "id",
  phone,
  accTypeId,
}: {
  fullName?: string;
  shortName?: string;
  propertyTypeId?: string;
  propertyStringValue?: string;
  externalId?: string;
  agreementId?: string;
  agreementNumber?: string;
  accountStatusId?: string;
  billDeliveryEmail?: string;
  id?: string;
  page: number;
  size?: number;
  sort?: string;
  phone?: string;
  accTypeId?: string;
}) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/accounts/pageable-search`,
    searchParams: {
      "organization.fullName": fullName,
      "organization.shortName": shortName,
      "organization.property.typeAlias": propertyTypeId,
      "organization.property.stringValue": propertyStringValue,
      "subscriber.externalId": externalId,
      "agreement.id": agreementId,
      "organization.phone": phone,
      accountStatusId,
      "agreement.agreementNumber": agreementNumber,
      billDeliveryEmail,
      id,
      page: page - 1,
      size,
      sort,
      clientType: "ORGANIZATION",
      accTypeId,
    },
  });

export const createAccountVersionRoute = ({
  accountId,
  fromDate,
}: {
  accountId: number;
  fromDate: string;
}) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/accounts/${accountId}`,
    searchParams: {
      fromDate,
    },
  });

export const getCreateIndividualContactPhoneRoute = (individualId: number | string) =>
  buildUrl({ url: `${baseUrl}/accounts-api/individuals/${individualId}/contact-phones` });

export const getCreateOrganizationContactPhoneRoute = (organizationId: number | string) =>
  buildUrl({ url: `${baseUrl}/accounts-api/organizations/${organizationId}/contact-phones` });

export const getUpdateAccountPhoneRoute = ({
  phoneId,
  fromDate,
  force,
}: {
  phoneId: number;
  fromDate: string;
  force: boolean;
}) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/contact-phones/${phoneId}`,
    searchParams: {
      fromDate,
      force,
    },
  });

export const getAccountPhonesVersionsRoute = (phoneId: number) =>
  buildUrl({ url: `${baseUrl}/accounts-api/contact-phones/${phoneId}/versions` });

export const getAccountPhonesRoute = (phoneId: number) =>
  buildUrl({ url: `${baseUrl}/accounts-api/contact-phones/${phoneId}` });

export const getAccountPhonesDeleteRoute = ({
  phoneId,
  fromDate,
}: {
  phoneId: number;
  fromDate: string;
}) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/contact-phones/${phoneId}`,
    searchParams: { fromDate },
  });

export const getIndividualUpdateRoute = ({
  individualId,
  fromDate,
  force,
}: {
  individualId: number;
  fromDate: MomentInput;
  force: boolean;
}) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/individuals/${individualId}`,
    searchParams: {
      fromDate,
      force,
    },
  });

export const getIndividualDeleteRoute = ({
  individualId,
  fromDate,
}: {
  individualId: number;
  fromDate: string;
}) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/individuals/${individualId}`,
    searchParams: {
      fromDate,
    },
  });

export const fetchUsersRoute = (props: { userId?: number[]; page?: number; size?: number }) => {
  const { userId, page = 0, size = 1000 } = props;
  return buildUrl({
    url: `${baseUrl}/security-api/users/pageable-search`,
    searchParams: {
      userId,
      page,
      size,
    },
  });
};

export const createOrganizationContactIndividualRoute = (organizationId: number | string) =>
  buildUrl({ url: `${baseUrl}/accounts-api/organizations/${organizationId}/contact-individual` });

export const getAccountInfoByIdRoute = (
  accountId: string,
  params: { onDate?: string; simpleView?: boolean }
) => buildUrl({ url: `${baseUrl}/accounts-api/accounts/${accountId}`, searchParams: params });

export const getIndividualInfoByIdRoute = (individualId: string, params: { onDate?: string }) =>
  buildUrl({ url: `${baseUrl}/accounts-api/individuals/${individualId}`, searchParams: params });

export const deleteIndividualByIdRoute = (individualId: number, params: { fromDate?: string }) =>
  buildUrl({ url: `${baseUrl}/accounts-api/individuals/${individualId}`, searchParams: params });

export const getEmailVersionsRoute = (emailId: number) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/contact-emails/${emailId}/versions`,
  });

export const createEmailRoute = (individualId, params: { fromDate?: string }) =>
  buildUrl({ url: `${baseUrl}/accounts-api/individuals/${individualId}/contact-emails` });

export const updateEmailVersionsRoute = (
  emailId: number,
  params: { fromDate?: MomentInput; force: boolean }
) => buildUrl({ url: `${baseUrl}/accounts-api/contact-emails/${emailId}`, searchParams: params });

export const closeEmailVersionsRoute = (emailId: number, params: { fromDate: string }) =>
  buildUrl({ url: `${baseUrl}/accounts-api/contact-emails/${emailId}`, searchParams: params });

export const createContactPhoneRoute = (individualId, params: { fromDate?: string }) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/individuals/${individualId}/contact-phones`,
    searchParams: params,
  });

export const getPhoneVersionsRoute = (phoneId: number) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/contact-phones/${phoneId}/versions`,
  });

export const updatePhoneVersionsRoute = (
  phoneId: number,
  params: { fromDate?: MomentInput; force: boolean }
) => buildUrl({ url: `${baseUrl}/accounts-api/contact-phones/${phoneId}`, searchParams: params });

export const closePhoneVersionsRoute = (phoneId: number, params: { fromDate: string }) =>
  buildUrl({ url: `${baseUrl}/accounts-api/contact-phones/${phoneId}`, searchParams: params });

export const getAddressVersionsRoute = (addressId: number) =>
  buildUrl({
    url: `${baseUrl}/accounts-api/addresses/${addressId}/versions`,
  });

export const updateAddressVersionsRoute = (
  addressId: number,
  params: { fromDate?: MomentInput; force: boolean }
) => buildUrl({ url: `${baseUrl}/accounts-api/addresses/${addressId}`, searchParams: params });

export const closeAddressVersionsRoute = (addressId: number, params: { fromDate?: string }) =>
  buildUrl({ url: `${baseUrl}/accounts-api/addresses/${addressId}`, searchParams: params });
